'use client';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/modals/Modal';
import api from '../utils/api';
import { DASHBOARD, USER_REQUESTS_API } from '../constants/routes';
import { AuthContext } from '../store/ContextStore';
import localStorageService from '../services/localStorageService';
import { AlertCircle } from "lucide-react";

const LockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="#ef4444" strokeWidth="1.5" fill="#FEE2E2" />
    <path d="M8 11V7a4 4 0 018 0v4" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1.5" fill="#ef4444" />
  </svg>
);

const TEAM_SEAT_OVERFLOW_MESSAGE_KEY = "team_seat_overflow_message";

export default function TeamSeatOverflow() {
  const auth = useContext(AuthContext);
  const openToast = (auth as any)?.openToast as ((message: string, color: string) => void) | undefined;
  const selectedSport = (auth as any)?.selectedSport || localStorageService.getSelectedSport("");

  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [requestText, setRequestText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sportCode = useMemo(() => String(selectedSport || "").trim(), [selectedSport]);
  const overflowMessage = useMemo(() => {
    const msg = localStorage.getItem(TEAM_SEAT_OVERFLOW_MESSAGE_KEY);
    return msg ? String(msg) : "";
  }, []);
  const navigate = useNavigate();

  // Redirect to dashboard if the current sport is no longer overflowed
  useEffect(() => {
    if (!sportCode) return;
    const userInfo = localStorageService.getUserInfo();
    const subscription = (userInfo as any)?.subscription;
    const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;

    // Check per-sport seat_locked status
    let hasOverflow = false;
    if (sportAccess && typeof sportAccess === 'object') {
      const selectedSportAccess = (sportAccess as any)[sportCode];
      hasOverflow = selectedSportAccess?.status === 'seat_locked';
    }

    // Also check global team_seat_info.limit_reached flag (set during login)
    const teamSeatInfo = (userInfo as any)?.team_seat_info || (auth as any)?.user?.team_seat_info;
    if (teamSeatInfo?.limit_reached) {
      hasOverflow = true;
    }

    if (!hasOverflow) {
      localStorage.removeItem(TEAM_SEAT_OVERFLOW_MESSAGE_KEY);
      navigate(DASHBOARD, { replace: true });
    }
  }, [sportCode, navigate]);

  async function handleSubmitRequest() {
    if (!sportCode) {
      openToast?.("Missing sport code. Please re-login and try again.", "red");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = { sport_code: sportCode };
      if (requestText.trim()) payload.request_text = requestText.trim();

      const res = await api.post(USER_REQUESTS_API, payload);
      const message = String(res?.data?.message || "Request submitted. Please wait for admin reply.");

      openToast?.(message, "green");
      setIsSubmitted(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === "string" && detail) ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit request. Please try again.";
      openToast?.(String(msg), "red");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div className="min-h-screen w-full bg-gradient-to-b from-[#E8EBF3] via-[#F0F3FA] to-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          {/* Icon */}
          <div className="w-[72px] h-[72px] rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
  <AlertCircle className="w-8 h-8 text-red-500" />
</div>


          {/* Title & Description */}
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Team Seat Limit Reached</h1>
          <p className="text-slate-500 text-sm leading-relaxed mb-6">
            {overflowMessage ||
              "Access is currently unavailable because your team has reached the maximum number of allowed users. Please contact your team administrator to request access."}
          </p>

          {/* Divider */}
          <div className="border-t border-slate-100 mb-6" />

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setIsRequestOpen(true)}
              className="mx-auto w-[200px] px-6 py-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold transition-all shadow-md"
            >
              Request Admin
            </button>
          </div>

        </div>
      </div>

      {/* Request Admin Modal */}
      <Modal
        isOpen={isRequestOpen}
        onClose={() => setIsRequestOpen(false)}
        showCloseButton={false}
        closeOnEsc={true}
        closeOnBackdrop={true}
        panelClassName="bg-white rounded-2xl shadow-xl p-10 max-w-2xl w-full text-left relative"
        backdropClassName="fixed inset-0 flex items-center justify-center bg-black/20 z-50 px-4"
      >
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Submit Admin Request</h2>
        <p className="text-slate-600 mb-6">
          Sport: <span className="font-semibold text-slate-800">{sportCode || "—"}</span>
        </p>

        <label className="block text-sm font-semibold text-slate-700 mb-2">Message (optional)</label>
        <textarea
          value={requestText}
          onChange={(e) => setRequestText(e.target.value)}
          rows={5}
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300"
          placeholder="Add any context for the admin…"
        />

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleSubmitRequest}
            disabled={isSubmitting || isSubmitted}
            className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting…" : isSubmitted ? "Request Submitted" : "Submit Request"}
          </button>
          <button
            type="button"
            onClick={() => setIsRequestOpen(false)}
            className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-all"
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}
